/**
 * Helpers for the BuzzFeed quiz (Skin Quiz / Fro-Yo Quiz) GA4 tracking.
 *
 * The two quizzes share the same component, so they share one tracking schema.
 * `quiz_name` (derived from the CMS `design` field) is the single source of
 * truth that separates them in GA4 reports — see the tracking schema sheet.
 */

/** "skin" -> "skin_quiz", "froyo" -> "froyo_quiz" */
export const getQuizName = (design?: string | null): string | null => (design ? `${design}_quiz` : null)

/** "skin" -> "Skin Quiz", "froyo" -> "Froyo Quiz" — used as the page_view title prefix. */
export const getQuizLabel = (design?: string | null): string => {
  if (!design) return "Quiz"
  return `${design.charAt(0).toUpperCase()}${design.slice(1)} Quiz`
}

/** GA4 ecommerce list id for quiz recommendations, e.g. "skin_quiz_recommendations". */
export const getQuizListId = (design?: string | null): string => {
  const quizName = getQuizName(design)
  return quizName ? `${quizName}_recommendations` : ""
}

/** Human-readable list name, e.g. "Skin Quiz Recommendations". */
export const getQuizListName = (design?: string | null): string => `${getQuizLabel(design)} Recommendations`

/** Virtual page path for a quiz funnel step, e.g. "/quiz/skin_quiz/q1". */
export const getQuizStepPath = (quizName: string, step: string): string => `/quiz/${quizName}/${step}`

/** "skin_quiz" -> "skin". Used on the result page where only quiz_name is known. */
export const getDesignFromQuizName = (quizName?: string | null): string => (quizName || "").replace(/_quiz$/, "")

/**
 * Normalise free text into a stable snake_case identifier for GA4 parameters
 * (question_id, answer, quiz_result). Lower-cased, non-alphanumerics collapsed
 * to single underscores, trimmed. e.g. "The Fresh Face Routine" -> "the_fresh_face_routine".
 */
export const toQuizSnakeCase = (value?: string | null): string =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

/**
 * Quiz result context handed from the quiz to its result page so the result page
 * can fire result_view / view_item_list / select_item.
 *
 * Passed via history `location.state` (navigation-scoped) rather than query params:
 * the result page is a normal route and Layout fires the canonical page_view on
 * navigation, so query params would be captured in that page_view's page_path /
 * page_location, fragmenting the results-page URL in GA4 and leaking quiz state.
 * History state keeps the URL clean and, unlike sessionStorage, cannot leak onto an
 * unrelated page that also renders the SkinRoutine section later in the session.
 */
export type QuizResultContext = {
  quiz_name: string
  quiz_result: string
  quiz_result_title: string
}
